/**
 * O texto dos e-mails do sistema, editado pelo painel (spec 041, 3.8): o editor (assunto, titulo, paragrafos e o texto do
 * botao, com as variaveis do e-mail a um clique) e a previa, que mostra o HTML que o servico de e-mails montou num iframe
 * isolado. Nenhum dos dois sabe a marca nem monta HTML: o editor so mexe no rascunho e a previa so exibe o que chegou.
 */
import { useId, useRef, type FocusEvent, type ReactNode } from "react";
import { Aviso } from "./Basicos";
import { Botao } from "./Botao";
import { Campo, CampoTextoLongo } from "./Campo";

export type ConteudoDeEmail = { titulo: string; paragrafos: string[]; cta?: { texto: string; url_var: string } };
export type RascunhoDeEmail = { assunto: string; conteudo: ConteudoDeEmail };

type Alvo = "assunto" | "titulo" | "cta" | number;

/**
 * Editor do rascunho. Clicar numa variavel escreve `{{variavel}}` onde o cursor estava (no ultimo campo tocado; sem
 * nenhum, no fim do ultimo paragrafo). `somenteLeitura` mostra o texto sem deixar mudar (papel sem permissao).
 */
export function EditorDeEmail({ valor, aoMudar, variaveis, somenteLeitura }: { valor: RascunhoDeEmail; aoMudar: (v: RascunhoDeEmail) => void; variaveis: string[]; somenteLeitura?: boolean }) {
  const idVariaveis = useId();
  const alvo = useRef<{ campo: Alvo; el: HTMLInputElement | HTMLTextAreaElement } | null>(null);
  const { assunto, conteudo } = valor;
  const mudarConteudo = (c: Partial<ConteudoDeEmail>) => aoMudar({ assunto, conteudo: { ...conteudo, ...c } });
  const mudarParagrafo = (i: number, texto: string) => mudarConteudo({ paragrafos: conteudo.paragrafos.map((p, j) => (j === i ? texto : p)) });
  const lerCampo = (c: Alvo) => (c === "assunto" ? assunto : c === "titulo" ? conteudo.titulo : c === "cta" ? conteudo.cta?.texto ?? "" : conteudo.paragrafos[c] ?? "");
  const escrever = (c: Alvo, texto: string) => {
    if (c === "assunto") aoMudar({ assunto: texto, conteudo });
    else if (c === "titulo") mudarConteudo({ titulo: texto });
    else if (c === "cta") mudarConteudo({ cta: conteudo.cta ? { ...conteudo.cta, texto } : undefined });
    else mudarParagrafo(c, texto);
  };
  const focar = (campo: Alvo) => (e: FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => { alvo.current = { campo, el: e.currentTarget }; };
  const inserir = (variavel: string) => {
    const marca = `{{${variavel}}}`;
    const ultimo = conteudo.paragrafos.length - 1;
    const a = alvo.current && alvo.current.el.isConnected ? alvo.current : null;
    const campo: Alvo = a?.campo ?? ultimo;
    const atual = lerCampo(campo);
    const inicio = a ? (a.el.selectionStart ?? atual.length) : atual.length;
    const fim = a ? (a.el.selectionEnd ?? inicio) : atual.length;
    escrever(campo, atual.slice(0, inicio) + marca + atual.slice(fim));
    // O cursor volta pro campo, logo depois da variavel, pra seguir escrevendo.
    if (a) requestAnimationFrame(() => { a.el.focus(); a.el.setSelectionRange(inicio + marca.length, inicio + marca.length); });
  };
  const remover = (i: number) => mudarConteudo({ paragrafos: conteudo.paragrafos.filter((_, j) => j !== i) });
  const leitura = { readOnly: somenteLeitura } as const;

  return (
    <div className="mtc-editor-email">
      <Campo rotulo="Assunto" valor={assunto} aoMudar={(v) => escrever("assunto", v)} onFocus={focar("assunto")} maxLength={300} {...leitura} />
      <Campo rotulo="Título" valor={conteudo.titulo} aoMudar={(v) => escrever("titulo", v)} onFocus={focar("titulo")} maxLength={300} dica="A primeira linha do e-mail, em destaque." {...leitura} />
      {conteudo.paragrafos.map((p, i) => (
        <div key={i} className="mtc-editor-email__paragrafo">
          <CampoTextoLongo rotulo={`Parágrafo ${i + 1}`} valor={p} aoMudar={(v) => escrever(i, v)} onFocus={focar(i)} maxLength={4000} linhas={3} {...leitura} />
          {!somenteLeitura && conteudo.paragrafos.length > 1 && (
            <Botao variante="fantasma" tamanho="pequeno" aria-label={`Remover o parágrafo ${i + 1}`} onClick={() => remover(i)}>Remover</Botao>
          )}
        </div>
      ))}
      {!somenteLeitura && conteudo.paragrafos.length < 30 && (
        <div><Botao variante="secundario" tamanho="pequeno" onClick={() => mudarConteudo({ paragrafos: [...conteudo.paragrafos, ""] })}>Adicionar parágrafo</Botao></div>
      )}
      {conteudo.cta && (
        <Campo rotulo="Texto do botão" valor={conteudo.cta.texto} aoMudar={(v) => escrever("cta", v)} onFocus={focar("cta")} maxLength={120} dica={<>O botão leva para <code className="mtc-mono">{`{{${conteudo.cta.url_var}}}`}</code>.</>} {...leitura} />
      )}
      {variaveis.length > 0 && (
        <div className="mtc-editor-email__variaveis">
          <span className="mtc-campo__rotulo" id={idVariaveis}>Variáveis deste e-mail</span>
          <div className="mtc-editor-email__fichas" role="group" aria-labelledby={idVariaveis}>
            {variaveis.map((v) => (
              <button
                key={v}
                type="button"
                className="mtc-ficha-variavel"
                disabled={somenteLeitura}
                // Sem roubar o foco: o cursor continua no campo que a pessoa estava editando.
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => inserir(v)}
                aria-label={`Inserir ${v}`}
              >
                {`{{${v}}}`}
              </button>
            ))}
          </div>
          <span className="mtc-campo__dica">Toque numa variável para escrever no lugar do cursor. Cada uma vira o dado do cliente no envio.</span>
        </div>
      )}
      <p className="mtc-campo__dica" style={{ margin: 0 }}>
        Formatação: <code className="mtc-mono">**negrito**</code>, <code className="mtc-mono">[texto](https://endereço)</code> e lista com <code className="mtc-mono">- </code> no começo de cada linha.
      </p>
    </div>
  );
}

/**
 * Previa do e-mail: o assunto em cima e o HTML num iframe isolado (`sandbox` sem `allow-scripts`: nada do e-mail roda,
 * nao abre formulario nem sai da moldura). O `allow-same-origin` so deixa a tela medir a altura do e-mail, pra moldura
 * crescer com ele em vez de rolar por dentro. `carregando` mantem a previa anterior a vista enquanto a nova chega.
 */
function ajustarAltura(quadro: HTMLIFrameElement) {
  const altura = quadro.contentDocument?.documentElement.scrollHeight;
  if (altura) quadro.style.height = `${altura}px`;
}

export function PreviaDeEmail({ assunto, html, carregando, erro, rodape }: { assunto?: string; html?: string; carregando?: boolean; erro?: ReactNode; rodape?: ReactNode }) {
  return (
    <div className="mtc-previa-email" aria-busy={carregando || undefined}>
      <div className="mtc-previa-email__assunto">
        <span className="mtc-rotulo">Assunto</span>
        <span className="mtc-previa-email__assunto-texto">{assunto ?? "..."}</span>
      </div>
      {erro && <Aviso tipo="erro">{erro}</Aviso>}
      <div className="mtc-previa-email__moldura">
        {html ? <iframe title="Prévia do e-mail" sandbox="allow-same-origin" srcDoc={html} className="mtc-previa-email__quadro" onLoad={(e) => ajustarAltura(e.currentTarget)} /> : <div className="mtc-previa-email__vazio">{carregando ? "Montando a prévia" : "Sem prévia ainda."}</div>}
        <span className="mtc-previa-email__estado" role="status" aria-live="polite">{carregando && html ? "Atualizando a prévia" : ""}</span>
      </div>
      {rodape}
    </div>
  );
}
